import { Injectable } from '@angular/core';
import { DbTransaction } from '../models/db/db-transaction';
import { DataService } from './data.service';
import { StoreName } from '../entities/StoreName';
import { StatusLookup } from '../entities/StatusLookup';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { InFilter } from '../baseServices/filter.interface';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { Subject } from 'rxjs';
import { Check, EntityHelper } from '../utils/entity-helper';

@Injectable({
  providedIn: 'root',
})
export class StatusLookupService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<StatusLookup>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(statusLookup: StatusLookup, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: statusLookup, who: who, what: what });
  }

  getEmitChangeCallback(statusLookup: StatusLookup, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForStatusLookup + '_' + statusLookup.RecordId,
      fn: this.emitChange.bind(this, statusLookup, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.StatusLookup], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getStatusLookupByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<StatusLookup> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<StatusLookup>(StoreName.StatusLookup, onlineId, StatusLookup, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as StatusLookup;

    if (this.checkIsStatusLookupSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getStatusLookupByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<StatusLookup> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<StatusLookup>(StoreName.StatusLookup, recordId, StatusLookup, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as StatusLookup;

    if (this.checkIsStatusLookupSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllStatusLookups(filter: any = undefined, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<StatusLookup[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll<StatusLookup>(StoreName.StatusLookup, filter, StatusLookup, transaction);

    let records = result as StatusLookup[];

    if (!includeDeleted) {
      records = records.filter(statusLookup => !this.checkIsStatusLookupSoftDeleted(statusLookup));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createStatusLookup(who: DataChangeEmitWho, recordToCreate: StatusLookup, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateStatusLookupCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.StatusLookup, transaction);

    // LC5. Get created record
    const createdRecord = await this.getStatusLookupByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateStatusLookup(who: DataChangeEmitWho, recordToUpdate: StatusLookup, skipLocalCopyValidation: boolean, localCopy: StatusLookup | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getStatusLookupByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateStatusLookupUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<StatusLookup>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<StatusLookup>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateStatusLookup(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.StatusLookup, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getStatusLookupByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateStatusLookup(statusLookup: StatusLookup, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateStatusLookupCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateStatusLookupUpdate(existingRecord: StatusLookup, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertStatusLookupFromSync(who: DataChangeEmitWho, recordToUpsert: StatusLookup, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getStatusLookupByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateStatusLookup(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createStatusLookup(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsStatusLookupSoftDeleted(record: StatusLookup): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: StatusLookup, b: StatusLookup): boolean {
    return a.Name === b.Name && a.Number === b.Number;
  }

  // #endregion
  // region Lookup

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
